/* eslint-disable jsx-a11y/no-noninteractive-element-interactions */
import moment from "moment";
import { useSelector } from "react-redux";
import { Typography, Box } from "@mui/material";
import React, { FunctionComponent, JSX } from "react";

import { RootState } from "../../../store";
import { tl } from "../../../components/translate";
import ClientInfo from "../../Client/ClientInfo/ClientInfo";
import ClientCreateEdit from "../../Client/ClientCreateEdit";
import { AssessmentComponent } from "./AssessmentComponents";
import { clientFullNameSelector } from "../../../reducers/client";
import useAssessmentLabel from "../../../hooks/useAssessmentLabel";
import { employeeFullNameSelector } from "../../../reducers/employee";
import { spFullNameSelector } from "../../../reducers/serviceProvider";
import { opdDurationFormatter } from "../../Reports/PatientAssessments";
import { convertADtoBS } from "../../../components/Calendar/functions/calendarFunctions";
import {
  Assessment,
  AssessmentDocument,
  Followup,
  IAuthorizedSigners
} from "../../../interfaces/AssessmentInterfaces";
import { ServiceProvider } from "../../../interfaces/ServiceProvidersInterface";

interface AssessmentContentProps {
  data: Assessment;
  // eslint-disable-next-line react/require-default-props
  assessmentDoc?: AssessmentDocument;
  showClientData?: boolean;
  showAttachment?: boolean;
}

export const calcInDays = (weeks: number): number => weeks * 7;

export const getFollowupFormat = (data: Followup): string => {
  const isPlural = (data.days ?? 1) < 2 ? "" : "s";
  switch (data.format) {
    case "days":
      return `day${isPlural}.`;
    case "weeks":
      return `week${isPlural}.`;
    case "months":
      return `month${isPlural}.`;
    default:
      return `year${isPlural}.`;
  }
};

const InfoRow = ({
  label,
  value
}: {
  label: React.ReactNode;
  value: React.ReactNode;
}): JSX.Element => (
  <Box display="flex">
    <Typography style={{ fontWeight: 600, width: "150px" }}>{label}</Typography>
    <Typography>{value}</Typography>
  </Box>
);

const getSignerInfo = (sp: ServiceProvider): string => {
  const name = spFullNameSelector(sp);
  const extra =
    sp.designation || [sp.qualification, sp.registrationNumber].filter(Boolean).join(", ");
  return extra ? `${name} (${extra})` : name;
};

const AssessmentContent: FunctionComponent<AssessmentContentProps> = ({
  data,
  assessmentDoc,
  showClientData = true,
  showAttachment = true
}) => {
  const assessmentLabels = useAssessmentLabel();
  const isClientPortal = useSelector((state: RootState) => state.isClientPortal);
  const resourceCentre = useSelector((state: RootState) => state.userContext.resourceCentre);

  const [showClientInfo, setShowClientInfo] = React.useState(false);
  const [clientEditMode, setClientEditMode] = React.useState(false);
  const assessmentDocument = assessmentDoc || data.assessmentDocument;
  const sortOrder = useSelector(
    (state: RootState) => state.userContext.resourceCentre?.settings?.assessmentSettings?.sortOrder
  );
  const serviceProviders = useSelector(
    (state: RootState) => state.resources.resourceCentreServiceProviders
  );

  const authorizedSigners: IAuthorizedSigners =
    assessmentDocument?.authorizedSigners || data?.authorizedSigners;

  const signerSPs = [
    authorizedSigners?.signerSp1,
    authorizedSigners?.signerSp2,
    authorizedSigners?.signerSp3
  ]
    .map((s) => serviceProviders?.find((sp: ServiceProvider) => sp.id === s?.id))
    .filter(Boolean) as ServiceProvider[];

  return (
    <Box
      sx={(theme) => ({
        [theme.breakpoints.down("md")]: {
          root: {
            padding: "16px"
          }
        }
      })}
    >
      <InfoRow
        label={tl("assessment.date")}
        value={convertADtoBS(moment(data.createdDate) || moment()).formatted2}
      />

      {showClientData && (
        <Box mb={2}>
          <InfoRow
            label={tl("assessment.assessedBy")}
            value={getSignerInfo(assessmentDocument.serviceProvider)}
          />

          <Box display="flex">
            <Typography sx={{ fontWeight: 600, width: "150px" }}>
              {tl("assessment.clientName")}
            </Typography>
            <Box
              onClick={() => {
                if (!isClientPortal) {
                  setShowClientInfo(true);
                }
              }}
              role="button"
              tabIndex={0}
              component={Typography}
              sx={{ cursor: "pointer", textDecoration: isClientPortal ? "none" : "underline" }}
            >
              {clientFullNameSelector(assessmentDocument.client).toUpperCase()}
            </Box>
          </Box>
          {data.claimCode && <InfoRow label="Claim Code" value={data.claimCode} />}

          {data?.referToSpId && data?.referToSp && (
            <InfoRow
              label={tl("assessment.referToSp")}
              value={spFullNameSelector(data.referToSp).toUpperCase()}
            />
          )}

          {data?.assessmentDocument?.preparedBy && (
            <InfoRow
              label={tl("assessment.preparedBy")}
              value={employeeFullNameSelector(assessmentDocument?.preparedBy)}
            />
          )}

          {data?.assessmentDocument?.opdDuration && (
            <InfoRow
              label={assessmentLabels.opdDuration}
              value={opdDurationFormatter(assessmentDocument?.opdDuration)}
            />
          )}

          {signerSPs.map((sp, index) => (
            <InfoRow
              key={sp.id}
              label={`Authorized Signer ${index + 1}`}
              value={getSignerInfo(sp)}
            />
          ))}
        </Box>
      )}

      <Box sx={{ borderBottom: "1px solid #ddd", marginBottom: "15px" }} />

      {sortOrder?.map((component: string) => {
        // Check if component should be shown (default to true if not set)
        const shouldShow =
          component === "eye"
            ? true
            : resourceCentre?.settings?.assessmentSettings?.showComponentsInPrint?.[component] ??
              true;
        if (!shouldShow) return null;

        return (
          <AssessmentComponent
            key={component}
            showAttachment={showAttachment}
            componentName={component}
            data={{
              ...assessmentDocument,
              attachments: data.attachments
            }}
            assessmentLabels={assessmentLabels}
          />
        );
      })}

      {showClientInfo && (
        <ClientInfo
          wrapperStyle={{ minWidth: "645px", maxWidth: "645px" }}
          id={data.clientId}
          handleViewClose={() => setShowClientInfo(false)}
          setEditMode={setClientEditMode}
          stayOnCurrentPage
        />
      )}

      {clientEditMode && (
        <ClientCreateEdit
          setEditMode={setClientEditMode}
          clientId={data.clientId}
          mode="edit"
          onCancel={() => {
            setClientEditMode(false);
          }}
          stayOnCurrentPage
        />
      )}
    </Box>
  );
};

export default AssessmentContent;
